// Invite link plumbing with NO crypto or API imports (INVITE-LINK-PASSKEY-
// FIRST.md §1.4): the router guard runs before anything else loads, so this
// stays tiny. The link is
//   https://<app>/accept-invite?o=<ownerUserId>&i=<inviteId>#k=<code>
// The code rides in the FRAGMENT, which never reaches a server. Sign-in /
// sign-up redirects can drop fragments (Login.vue rewrites `redirect` in
// places), so the guard stashes {o, i, k} in sessionStorage BEFORE any auth
// redirect and the accept page reads it back afterwards.

export const INVITE_ROUTE = '/accept-invite'
const STASH_KEY = 'nc1.invite.pending'
const CODE_RE = /^[A-Za-z0-9_-]{20,64}$/

/**
 * `loginAs` = the invited email: the login page prefills it, and a browser
 * signed in as someone else is signed out and told to use that address (the
 * existing loginAs mechanism) — the invite is served only to that account.
 */
export function buildInviteUrl ({ ownerUserId, inviteId, code, recipientEmail }) {
  const loginAs = recipientEmail ? `&loginAs=${encodeURIComponent(recipientEmail)}` : ''
  return `${window.location.origin}${INVITE_ROUTE}?o=${encodeURIComponent(ownerUserId)}&i=${encodeURIComponent(inviteId)}${loginAs}#k=${code}`
}

/** The code out of a hash ("#k=…", with or without other params) or a bare code; null otherwise. */
export function parseInviteCode (hashOrCode) {
  const s = String(hashOrCode || '')
  const m = /(?:^|[#&?])k=([A-Za-z0-9_-]+)/.exec(s)
  const code = m ? m[1] : s
  return CODE_RE.test(code) ? code : null
}

export function inviteFromRoute (route) {
  const q = (route && route.query) || {}
  return {
    o: typeof q.o === 'string' && q.o ? q.o : null,
    i: typeof q.i === 'string' && q.i ? q.i : null,
    k: parseInviteCode(route && route.hash),
  }
}

export function loadStashedInvite () {
  try {
    const raw = sessionStorage.getItem(STASH_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

export function clearStashedInvite () {
  try { sessionStorage.removeItem(STASH_KEY) } catch (e) { /* storage unavailable */ }
}

/** Router guard helper: remember the link's parts (keeping an earlier code when this hit lost it). */
export function stashInviteFromRoute (route) {
  try {
    const inv = inviteFromRoute(route)
    if (!inv.o || !inv.i) return
    const prev = loadStashedInvite()
    const k = inv.k || (prev && prev.o === inv.o && prev.i === inv.i ? prev.k : null)
    sessionStorage.setItem(STASH_KEY, JSON.stringify({ o: inv.o, i: inv.i, k, at: Date.now() }))
  } catch (e) { /* storage unavailable */ }
}

/** What the accept page works with: the route's parts, with the stash filling a missing code. */
export function resolveInvite (route) {
  const fromRoute = inviteFromRoute(route)
  const stash = loadStashedInvite()
  if (fromRoute.o && fromRoute.i) {
    const sameInvite = stash && stash.o === fromRoute.o && stash.i === fromRoute.i
    return { ...fromRoute, k: fromRoute.k || (sameInvite ? stash.k : null) }
  }
  return stash && stash.o && stash.i ? { o: stash.o, i: stash.i, k: stash.k || null } : fromRoute
}
